import * as d3 from 'd3';

// Constants for zoom behavior
export const ZOOM_CONFIG = {
  // Zoom extents
  MIN_SCALE: 0.1,
  MAX_SCALE: 3,
  DEFAULT_SCALE: 1.5,
  
  // Padding and safety factors
  FIT_PADDING: 80, // Padding around content when calculating fit zoom
  SAFETY_FACTOR: 0.85, // General safety reduction for calculated zoom
  
  // Operation-specific zoom factors
  MANUAL_CLICK_FACTOR: 0.92, // Zoom factor for manual node clicks
  EXPAND_ONE_LEVEL_FACTOR: 0.45, // Zoom factor for expand one level (more aggressive zoom out)
  EXPAND_ALL_FACTOR: 0.15, // Zoom factor for expand all (extremely aggressive zoom out)
  
  // Fallback zooms when calculation fails
  FALLBACK_ZOOM: 0.35,
  MANUAL_CLICK_FALLBACK: 0.4,
  EXPAND_ONE_LEVEL_FALLBACK: 0.4,
  EXPAND_ALL_FALLBACK: 0.25,
  
  // Minimum zoom thresholds
  MIN_FINAL_ZOOM: 0.1,
  MIN_MANUAL_ZOOM: 0.15,
  
  // Animation durations
  ZOOM_TRANSITION_DURATION: 750,
  ZOOM_DELAY: 300,
  COLLAPSE_ZOOM_DELAY: 200
};

export interface ZoomUtilsConfig {
  svgRef: React.RefObject<SVGSVGElement>;
  zoomRef: React.RefObject<d3.ZoomBehavior<SVGSVGElement, unknown>>;
}

export class ZoomUtils {
  private svgRef: React.RefObject<SVGSVGElement>;
  private zoomRef: React.RefObject<d3.ZoomBehavior<SVGSVGElement, unknown>>;

  constructor(config: ZoomUtilsConfig) {
    this.svgRef = config.svgRef;
    this.zoomRef = config.zoomRef;
  }

  /**
   * Apply zoom for manual node click (expansion/collapse)
   */
  applyManualClickZoom(willExpand: boolean): void {
    if (!this.zoomRef.current || !this.svgRef.current) return;

    setTimeout(() => {
      try {
        const svg = d3.select(this.svgRef.current!);
        const currentZoom = d3.zoomTransform(this.svgRef.current!).k;
        
        let finalZoom: number;
        
        if (willExpand) {
          // EXPANDING: Zoom OUT slightly to show new content (reduce zoom level)
          finalZoom = Math.max(currentZoom * ZOOM_CONFIG.MANUAL_CLICK_FACTOR, ZOOM_CONFIG.MIN_MANUAL_ZOOM);
        } else {
          // COLLAPSING: Zoom IN slightly since there's less content (increase zoom level)
          finalZoom = Math.min(currentZoom * 1.08, ZOOM_CONFIG.MAX_SCALE * 0.8);
        }
        
        svg.transition()
          .duration(ZOOM_CONFIG.ZOOM_TRANSITION_DURATION)
          .call(this.zoomRef.current!.scaleTo, finalZoom);
      } catch (error) {
        // Fallback zoom if calculation fails
        const svg = d3.select(this.svgRef.current!);
        svg.transition()
          .duration(ZOOM_CONFIG.ZOOM_TRANSITION_DURATION)
          .call(this.zoomRef.current!.scaleTo, ZOOM_CONFIG.MANUAL_CLICK_FALLBACK);
      }
    }, ZOOM_CONFIG.ZOOM_DELAY);
  }

  /**
   * Apply zoom for expand one level operation
   */
  applyExpandOneLevelZoom(expandedNodesCount: number): void {
    if (!this.zoomRef.current || !this.svgRef.current) return;

    setTimeout(() => {
      try {
        const svg = d3.select(this.svgRef.current!);
        const currentZoom = d3.zoomTransform(this.svgRef.current!).k;
        
        // Since we're expanding multiple nodes, zoom OUT significantly (reduce zoom level)
        const finalZoom = Math.max(currentZoom * ZOOM_CONFIG.EXPAND_ONE_LEVEL_FACTOR, ZOOM_CONFIG.MIN_MANUAL_ZOOM);
        
        svg.transition()
          .duration(ZOOM_CONFIG.ZOOM_TRANSITION_DURATION)
          .call(this.zoomRef.current!.scaleTo, finalZoom);
      } catch (error) {
        const svg = d3.select(this.svgRef.current!);
        svg.transition()
          .duration(ZOOM_CONFIG.ZOOM_TRANSITION_DURATION)
          .call(this.zoomRef.current!.scaleTo, ZOOM_CONFIG.EXPAND_ONE_LEVEL_FALLBACK);
      }
    }, ZOOM_CONFIG.ZOOM_DELAY);
  }

  /**
   * Apply zoom for expand all operation
   */
  applyExpandAllZoom(): void {
    if (!this.zoomRef.current || !this.svgRef.current) return;

    setTimeout(() => {
      try {
        const svg = d3.select(this.svgRef.current!);
        const currentZoom = d3.zoomTransform(this.svgRef.current!).k;
        
        // Since we're expanding everything, zoom OUT very aggressively
        const finalZoom = Math.max(currentZoom * ZOOM_CONFIG.EXPAND_ALL_FACTOR, ZOOM_CONFIG.MIN_FINAL_ZOOM);
        
        svg.transition()
          .duration(ZOOM_CONFIG.ZOOM_TRANSITION_DURATION)
          .call(this.zoomRef.current!.scaleTo, finalZoom);
      } catch (error) {
        const svg = d3.select(this.svgRef.current!);
        svg.transition()
          .duration(ZOOM_CONFIG.ZOOM_TRANSITION_DURATION)
          .call(this.zoomRef.current!.scaleTo, ZOOM_CONFIG.EXPAND_ALL_FALLBACK);
      }
    }, ZOOM_CONFIG.ZOOM_DELAY);
  }

  /**
   * Reset zoom to default level
   */
  resetToDefault(): void {
    if (!this.zoomRef.current || !this.svgRef.current) return;

    setTimeout(() => {
      const svg = d3.select(this.svgRef.current!);
      svg.transition().duration(ZOOM_CONFIG.ZOOM_TRANSITION_DURATION).call(
        this.zoomRef.current!.transform,
        d3.zoomIdentity.scale(ZOOM_CONFIG.DEFAULT_SCALE)
      );
    }, ZOOM_CONFIG.COLLAPSE_ZOOM_DELAY);
  }

  /**
   * Zoom in by factor
   */
  zoomIn(): void {
    if (!this.svgRef.current) return;
    const svg = d3.select(this.svgRef.current);
    svg.transition().duration(300).call(
      this.zoomRef.current!.scaleBy, 1.5
    );
  }

  /**
   * Zoom out by factor
   */
  zoomOut(): void {
    if (!this.svgRef.current) return;
    const svg = d3.select(this.svgRef.current);
    svg.transition().duration(300).call(
      this.zoomRef.current!.scaleBy, 0.67
    );
  }

  /**
   * Reset zoom to identity (1x scale, no translation)
   */
  resetZoom(): void {
    if (!this.svgRef.current) return;
    const svg = d3.select(this.svgRef.current);
    svg.transition().duration(500).call(
      this.zoomRef.current!.transform,
      d3.zoomIdentity
    );
  }

  /**
   * Pan to ensure expanded children are visible on screen
   * @param parentNode The parent node that was just expanded
   * @param transitionDuration Duration of the node expansion transition
   */
  ensureChildrenVisible(parentNode: any, transitionDuration: number = 250): void {
    if (!this.svgRef.current || !this.zoomRef.current || !parentNode.children) return;

    // Wait for the node positions to be calculated and rendered
    setTimeout(() => {
      try {
        const svg = d3.select(this.svgRef.current!);
        const svgElement = this.svgRef.current!;
        const currentTransform = d3.zoomTransform(svgElement);
        
        // Get SVG viewport dimensions
        const svgRect = svgElement.getBoundingClientRect();
        const svgHeight = svgRect.height;
        const svgWidth = svgRect.width;
        
        // Get all children nodes
        const children = parentNode.children;
        if (!children || children.length === 0) return;
        
        // Find the bottommost child (the one that might be cut off)
        let bottomMostChild = children[0];
        let maxY = children[0].y || 0;
        
        children.forEach((child: any) => {
          if (child.y !== undefined && child.y > maxY) {
            maxY = child.y;
            bottomMostChild = child;
          }
        });
        
        // Transform child coordinates to screen coordinates
        const childScreenY = (bottomMostChild.y * currentTransform.k) + currentTransform.y;
        
        // Add padding for the node size and labels
        const nodePadding = 80;
        const childBottomScreen = childScreenY + nodePadding;
        
        console.log('Checking child visibility:', {
          childY: bottomMostChild.y,
          childScreenY,
          childBottomScreen,
          svgHeight,
          needsScroll: childBottomScreen > svgHeight
        });
        
        // Check if the bottom of the child is below the visible area
        if (childBottomScreen > svgHeight) {
          // Calculate how much we need to pan up (negative Y) to show the children
          const overflow = childBottomScreen - svgHeight;
          const newY = currentTransform.y - overflow - 50; // Extra 50px margin from bottom
          
          console.log('Panning to show children:', {
            overflow,
            currentY: currentTransform.y,
            newY
          });
          
          // Apply the new transform with smooth transition
          const newTransform = d3.zoomIdentity
            .translate(currentTransform.x, newY)
            .scale(currentTransform.k);
          
          svg.transition()
            .duration(ZOOM_CONFIG.ZOOM_TRANSITION_DURATION)
            .call(this.zoomRef.current!.transform, newTransform);
        } else {
          console.log('Children are already visible, no panning needed');
        }
      } catch (error) {
        console.error('Error ensuring children visibility:', error);
      }
    }, transitionDuration + 200); // Wait for node expansion transition + small buffer
  }
}
